import { Schedule } from '@mui/icons-material';
import { Grid2, Card, Typography, Chip } from '@mui/material';
import { StudentJobOffer } from '@thro-types/jobmarket';

import { format } from 'date-fns';
import { t } from 'i18next';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';

import { useStudentJobOfferingsMutation } from 'api/hooks/jobmarket/studentOffers';
import { ConfirmationDialog } from 'app/components';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { ACLHelper } from 'utils/ACLHelper';
import { useAuthContext } from 'utils/hooks/useAuthContext';
import { keepContentScrollPosition } from 'utils/hooks/useContentScrollPosition';
import { useCurrentURL } from 'utils/hooks/useCurrentURL';

import { Menu } from '../menu';

interface Props {
  studentOffering: StudentJobOffer;
  handleEdit: (value: StudentJobOffer) => void;
}

export const StudentJobOfferCard = ({ studentOffering, handleEdit }: Props) => {
  const currentURL = useCurrentURL();
  const dispatch = useDispatch();
  const { user } = useAuthContext();

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const aclHelper = new ACLHelper(user);
  const canEdit = studentOffering.userId === user.id || aclHelper.isSuperUser();

  const { deletion } = useStudentJobOfferingsMutation();

  const removeHtmlTags = (value: string) => {
    const regex = /(<([^>]+)>)/gi;
    return value.replace(regex, '');
  };

  const handleEditAction = () => {
    handleEdit(studentOffering);
  };

  const handleDelete = async () => {
    try {
      if (!studentOffering.id) {
        return;
      }
      await deletion.mutateAsync(studentOffering.id);
      dispatch(AlertActions.success(t(translations.pages.studentJobOffers.alerts.deleteSuccess())));
    } catch {
      dispatch(AlertActions.error(t(translations.pages.studentJobOffers.alerts.deleteFailed())));
    } finally {
      setDeleteDialogOpen(false);
    }
  };

  return (
    <>
      <ConfirmationDialog
        title={t(translations.common.reallyDelete())}
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        isPending={deletion.isPending}
      >
        {t(translations.pages.studentJobOffers.dialogs.deleteConfirm())}
      </ConfirmationDialog>
      <Grid2 container size={{ xs: 12, md: 12, lg: 6, xl: 4 }}>
        <Card sx={{ display: 'flex', flex: 1 }}>
          <Link
            to={`/jobs/studentOffers/${studentOffering.id}`}
            state={{ back: currentURL }}
            style={{ display: 'flex', flex: 1 }}
            onClick={keepContentScrollPosition}
          >
            <Grid2
              container
              sx={{
                padding: '1.5em',
                flexDirection: 'column',
                flex: 1
              }}
              justifyContent={'space-between'}
            >
              <Grid2 container justifyContent="space-between" wrap="nowrap">
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 'bold', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                >
                  {studentOffering.title}
                </Typography>
                <Grid2
                  container
                  alignItems="center"
                  gap={1}
                  sx={{ marginLeft: 'auto' }}
                  justifyContent={'flex-end'}
                  size={8}
                  height={40}
                  minWidth={60}
                >
                  <Chip
                    key={studentOffering.course?.id}
                    label={studentOffering.course?.shorthand}
                    variant="outlined"
                    color="primary"
                    size="small"
                  />
                  {canEdit && (
                    <Grid2 alignItems="flex-start">
                      <Menu
                        handleEdit={handleEditAction}
                        handleDelete={() => setDeleteDialogOpen(true)}
                      />
                    </Grid2>
                  )}
                </Grid2>
              </Grid2>
              <Grid2>
                <Typography variant="subtitle2" sx={{ color: 'text.secondary', mt: 1 }}>
                  {studentOffering?.jobType?.name}
                </Typography>
              </Grid2>

              <Grid2 sx={{ mb: 1.5 }}>
                <Typography
                  variant="body1"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    wordBreak: 'break-word',
                    mt: 1
                  }}
                >
                  {removeHtmlTags(studentOffering.description)}
                </Typography>
              </Grid2>

              <Grid2 container>
                <Schedule sx={{ color: '#37b200', mr: 0.5 }} />
                <Typography variant="body1">
                  {t(translations.pages.jobOffers.start()) + ': '}
                  <strong>{format(new Date(studentOffering.startDate), 'dd.MM.yyyy')}</strong>
                </Typography>
              </Grid2>
            </Grid2>
          </Link>
        </Card>
      </Grid2>
    </>
  );
};
